import type { ReadBranchType } from '@readme/api-contracts/schemas/branch';

import { useEffect, useMemo } from 'react';

import useClassy from '@core/hooks/useClassy';
import usePlanPageUrl from '@core/hooks/usePlanPageUrl';
import useUniqueId from '@core/hooks/useUniqueId';
import { useProjectStore, useSuperHubStore } from '@core/store';
import PlanAccessBadge from '@routes/SuperHub/components/PlanAccess/Badge';
import Button from '@ui/Button';
import Flex from '@ui/Flex';
import Icon from '@ui/Icon';
import { RHFGroup } from '@ui/RHF';
import { showToast } from '@ui/Toast';

import BranchNameInput from '../components/BranchNameInput';
import { useBlockingModalContext } from '../Modals';

import { useCreateBranchFormContext, useCreateBranchMetaContext } from './Context';
import styles from './Form.module.scss';
import { BranchErrorNotification, BranchSuccessNotification } from './notifications';
import { validateSimpleBranchName } from './utils';

export interface CreateBranchFormProps {
  className?: string;
  /**
   * Method to run upon success of branch creation. If no custom success
   * handling is provided, the default handling will be to display a success
   * toast.
   */
  onSuccess?: (response: ReadBranchType) => Promise<void>;
}

export default function CreateBranchForm({ className, onSuccess }: CreateBranchFormProps) {
  const bem = useClassy(styles, 'CreateBranchForm');
  const uid = useUniqueId('CreateBranchForm');

  // branch paygate
  const [isTrialExpired, planFeatures] = useProjectStore(s => [s.plan.isTrialExpired, s.plan.features]);
  const planHasBranches = planFeatures.branches && !isTrialExpired;
  const planPageUrl = usePlanPageUrl();

  const { source } = useCreateBranchMetaContext();
  const formValue = useCreateBranchFormContext();
  const { setBlockingModalValue } = useBlockingModalContext();
  const [ephemeralBranch, versionsMap, createBranch] = useSuperHubStore(s => [
    s.branches.ephemeralBranch,
    s.branches.versionsMap,
    s.branches.createBranch,
  ]);

  /**
   * The list of existing branches for the version we're currently creating a
   * new branch from.
   */
  const branches = useMemo(() => {
    if (ephemeralBranch) {
      return versionsMap[ephemeralBranch.base]?.branches?.data || [];
    }
    return [];
  }, [ephemeralBranch, versionsMap]);

  // On initial load, pre-fill the name input from `ephemeralBranch.name` (set
  // by surfaces like the BranchesMenu zero-results "Create branch '<query>'"
  // CTA — the search query becomes the seed name) and focus it. When no
  // pre-fill is provided the field is empty and we just focus.
  useEffect(() => {
    if (ephemeralBranch?.name) {
      formValue.setValue('name', ephemeralBranch.name);
    }
    formValue.setFocus('name');
  }, [formValue, ephemeralBranch?.name]);

  const handleSubmit = formValue.handleSubmit(async data => {
    // this should never happen since we don't render the form if
    // ephemeralBranch is null, but being extra safe for the compiler
    if (!ephemeralBranch) {
      return;
    }

    const fullBranchName = `${ephemeralBranch.base}_${data.name}`;
    // Trigger a form field error if branch name is not unique.
    if (branches.some(branch => branch.name === fullBranchName)) {
      formValue.setError('name', {
        type: 'value',
        message: 'Branch already exists.',
      });
      return;
    }

    // Trigger a form field error if branch name is not a valid git name
    // according to our simplified rules.
    const nameValidity = validateSimpleBranchName(data.name);
    if (!nameValidity.isValid) {
      formValue.setError('name', {
        type: 'value',
        message: nameValidity.error,
      });
      return;
    }

    try {
      setBlockingModalValue('Creating Branch…');
      const response = await createBranch({ name: fullBranchName });

      if (onSuccess) {
        await onSuccess(response);
      } else {
        showToast(<BranchSuccessNotification name={response.data.name} />);
      }
    } catch (error) {
      showToast(<BranchErrorNotification apiError={error} name={fullBranchName} />);
      setBlockingModalValue('');
    }
  });

  if (!ephemeralBranch) {
    return null;
  }

  return (
    <form
      autoComplete="off"
      name="create-branch-form"
      onSubmit={e => {
        e.stopPropagation();
        handleSubmit(e);
      }}
    >
      <Flex className={bem('&', `_${source}`, className)} gap="sm" layout="col">
        {!planHasBranches && (
          <Flex className={bem('-paygate')} gap="size-8" justify="start" layout="col">
            <Flex gap="size-4" justify="start" layout="col">
              <PlanAccessBadge access="locked" kind="outline" plan="pro2026" />
              <p className={bem('-paygate-p')}>Upgrade to a Pro plan to create new branches.</p>
            </Flex>
            <Button href={planPageUrl.url} kind="primary" rel="noreferrer" size="sm" target={planPageUrl.target}>
              See Plans
              <Icon aria-label="Opens in new tab" name="arrow-up-right" />
            </Button>
          </Flex>
        )}
        {!!planHasBranches && (
          <>
            <RHFGroup
              className={bem('-name-form-group')}
              control={formValue.control}
              id={uid('name')}
              label="Branch Name"
              name="name"
              required
            >
              {({ field }) => <BranchNameInput baseVersion={ephemeralBranch.base} {...field} />}
            </RHFGroup>

            <div className={bem('-divider')} />

            <Button
              className={bem('-button')}
              fullWidth
              loading={formValue.formState.isSubmitting ? 'Creating Branch' : false}
              size="sm"
              type="submit"
            >
              Create Branch
            </Button>
          </>
        )}
      </Flex>
    </form>
  );
}
